"use client";

import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Button, Badge } from "react-bootstrap";
import { parse, format, differenceInMonths, differenceInDays, addMonths } from "date-fns";
import { ru } from "date-fns/locale";
import LifeParts from "../../components/lifepart/lifeParts";
import { useData } from "../../storage/accessors";
import ButtonDatePicker from "../../components/datePicker";
import Icon from "../../components/Icon";
import { faCalendarDays, faTrash } from '@fortawesome/free-solid-svg-icons';



export default function Home() {
    const { wheels } = useData();
    const wheel = wheels.objects['wheel'];
    const rawDeadline = wheel?.dateDescription || "";

    const safeParse = (str: string): Date | null => {
        try {
            const d = parse(str, "yyyy-MM-dd", new Date());
            return isNaN(d.getTime()) ? null : d;
        } catch {
            return null;
        }
    };

    const deadline = safeParse(rawDeadline) ? rawDeadline : "";
    const [parsedDate, setParsedDate] = useState<Date>(
        deadline ? safeParse(deadline)! : new Date()
    );

    useEffect(() => {
        if (wheel?.dateDescription) {
            const d = safeParse(wheel.dateDescription);
            if (d) {
                setParsedDate(d);
            } else {
                // Неверный формат — считаем, что срок не установлен
                wheels.save('wheel', { dateDescription: "" });
                setParsedDate(new Date());
            }
        }
    }, [wheel?.dateDescription]);

    const remainingText = useMemo(() => {
        if (!deadline) return "";
        const deadlineDate = safeParse(deadline);
        if (!deadlineDate) return "";
        const now = new Date();

        const months = differenceInMonths(deadlineDate, now);
        const days = differenceInDays(deadlineDate, now);

        if (months < 1 && days <= 0) return "Срок вышел";
        if (months < 1) return `${days} дн.`;

        // Полные месяцы + оставшиеся дни
        const afterMonths = addMonths(now, months);
        const extraDays = differenceInDays(deadlineDate, afterMonths);

        if (months >= 12) {
            const years = Math.floor(months / 12);
            const remMonths = months % 12;
            if (remMonths > 0) {
                if (extraDays > 0) return `${years} г. ${remMonths} мес. и ${extraDays} дн.`;
                return `${years} г. ${remMonths} мес.`;
            }
            if (extraDays > 0) return `${years} г. и ${extraDays} дн.`;
            return `${years} г.`;
        }
        if (extraDays > 0) return `${months} мес. и ${extraDays} дн.`;
        return `${months} мес.`;
    }, [deadline]);

    const handleDeadlineSelect = (date: Date | null) => {
        if (date) {
            const formatted = format(date, "yyyy-MM-dd");
            wheels.save('wheel', { dateDescription: formatted });
            setParsedDate(date);
        }
    };

    const clearDeadline = () => {
        wheels.save('wheel', { dateDescription: "" });
        setParsedDate(new Date());
    };

    return (
        <>
            <Row className="mb-4">
                <Col>
                    <h2>Баланс</h2>
                    <p className="text-muted">Управляйте частями вашей жизни и отслеживайте их развитие</p>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-2">
                    {deadline ? (
                        <>
                            <span className="text-muted small">Срок:</span>
                            <Badge bg="secondary">{remainingText}</Badge>
                            <span className="text-muted small">
                                ({format(parsedDate, "dd.MM.yyyy", { locale: ru })})
                            </span>
                            <ButtonDatePicker
                                date={parsedDate}
                                onSelect={handleDeadlineSelect}
                                icon={faCalendarDays}
                                title="Изменить срок"
                                inputProps={{ variant: "outline-secondary" }}
                            />
                            <Button size="sm" variant="outline-danger" onClick={clearDeadline} title="Убрать срок">
                                <Icon icon={faTrash} />
                            </Button>
                        </>
                    ) : (
                        <>
                            <span className="text-muted small">Выбрать срок</span>
                            <ButtonDatePicker
                                date={parsedDate}
                                onSelect={handleDeadlineSelect}
                                icon={faCalendarDays}
                                title="Установить срок"
                                inputProps={{ variant: "outline-secondary" }}
                            />
                        </>
                    )}
                </Col>
            </Row>
            <LifeParts />
        </>
    )
}